import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";

/**
 * Ordered categorical palette for charts whose series are *categories* rather than sources.
 *
 * Series colour must be assigned from a stable, response-level category order — never from the order
 * labels happen to appear in a row — or the same currency changes colour between two portfolios.
 * Neutrals (`N*`) are excluded: they are the chrome, and a category drawn in chrome colour reads as
 * "no data".
 */
export const categoricalChartPalette: ReadonlyArray<string> = [
	themeCSSVars.palette_S400,
	themeCSSVars.palette_B400,
	themeCSSVars.palette_P400,
	themeCSSVars.palette_A400,
	themeCSSVars.palette_D400,
	themeCSSVars.palette_V400,
	themeCSSVars.palette_R400,
	themeCSSVars.palette_S600,
	themeCSSVars.palette_B600,
	themeCSSVars.palette_P600,
	themeCSSVars.palette_A600,
	themeCSSVars.palette_D600,
	themeCSSVars.palette_V600,
	themeCSSVars.palette_R600,
];

/** Colour for the aggregated tail. Deliberately neutral — "Other" is not a category. */
export const othersChartColor = themeCSSVars.palette_N300;

/**
 * Opacity of the CURRENT series while "Highlight proposal" is on, so that all four chart kinds dim it by
 * the same amount — `MetricComparisonChart` spells the same value as Tailwind's `opacity-40` on its points.
 *
 * Where it goes is per chart, and not interchangeable. Highcharts' `series.opacity` lands on the series'
 * plot group *and*, through `pointAttribs`, on every rect inside it — so setting it dims twice (0.4 → 0.16
 * on screen). `StackedMetricChart` therefore resolves it into each point's own `opacity`. Either way the
 * legend swatch is untouched, so a dimmed series still reads as itself in the legend.
 */
export const dimmedSourceOpacity = 0.4;

/**
 * Colour of the key-metric threshold line (FR-8), on whichever axis a chart kind draws it.
 *
 * Deliberately a translucent neutral rather than a palette entry: the threshold is a *reference the user
 * set*, not a series, and the two distribution kinds draw their own series-coloured lines (the per-series
 * mean) on the same axis — the threshold has to read as a different sort of thing at a glance. The literal
 * is `MetricComparisonChart`'s, so the line looks the same wherever it lands.
 */
export const keyMetricThresholdColor = "rgba(135, 146, 171, 0.5)";

export function categoricalChartColor(index: number): string {
	return categoricalChartPalette[index % categoricalChartPalette.length];
}
